import { ACCESS_TOKEN_KEY } from "@app/constants";
import HttpService from "@core/services/http/http.service";
import StorageService from "@core/services/storage";
import {
  Book,
  BookAudioAccessInfo,
  BookAudioDetail,
  BookDetail,
  BookDetailAuthor,
  BookEbookAccessInfo,
  BookMediaType,
  BookElementChildrenDetail,
  BookElementDetail,
  BookElementHighlightTextDetail,
  BookNoteDetail,
  BooksByTags,
  CreateBook,
  EbookContent,
  EbookManifest,
  EbookNote,
  EbookProgress,
  EbookSession,
  GetAllResponse,
  GetBookOptionRequestParams,
  GetDetailResponse,
  GetListResponse,
  TypeOfBook,
} from "@app/types";
import {
  RequestContentType,
  RequestParams,
  Signal,
} from "@core/services/http/http.type";
import { Environment } from "@environments/environment";
import axios from "axios";

type BookInsight = BookDetail["insight"];
type BookCategory = BookDetail["categories"][number];
type BookBackgroundMusic = BookDetail["backgroundMusic"];

interface BookElementV3Raw {
  id?: number;
  name?: string;
  description?: string | null;
  content?: string;
  linkAudio?: string;
  level?: number;
  order?: number;
  point?: number;
  isLock?: boolean;
  audioDuration?: number;
  child?: unknown;
  audios?: unknown;
  bookNotes?: unknown;
  highlightTexts?: unknown;
}

interface BookDetailV3Raw {
  id?: number;
  available?: boolean;
  isAvailable?: boolean;
  linkStore?: string;
  categories?: unknown;
  backgroundMusic?: BookBackgroundMusic;
  name?: string;
  description?: string;
  price?: number;
  oldPrice?: number;
  bookOption?: number;
  thumbnails?: unknown;
  author?: string;
  authors?: unknown;
  isPopular?: boolean;
  isFavorite?: boolean;
  isTryListening?: boolean;
  releaseType?: number;
  totalPoint?: number;
  totalTime?: number;
  elements?: unknown;
  top30Book?: unknown;
  insight?: Partial<BookInsight>;
  mediaType?: number;
  audio?: unknown;
  ebook?: unknown;
}

interface BookDetailRequestOptions {
  useCache?: boolean;
  forceRefresh?: boolean;
}

class _BookService {
  private readonly APP_BUCKET_URL_STORAGE_KEY = "app_bucket_url";

  private readonly APP_BASE_URL_STORAGE_KEY = "app_base_url_storage";

  private readonly BASE_URL_STORAGE_KEY = "base_url_storage";

  private readonly bookDetailCache = new Map<number, GetDetailResponse<BookDetail>>();

  private normalizeAudioInfo(value: unknown): BookAudioAccessInfo | undefined {
    if (!value || typeof value !== "object") {
      return undefined;
    }

    const source = value as Record<string, unknown>;

    return {
      enabled: Boolean(source.enabled),
      hasAccess: Boolean(source.hasAccess),
      canPurchase: Boolean(source.canPurchase),
      price: Number(source.price ?? 0),
      oldPrice: Number(source.oldPrice ?? 0),
      totalTime: Number(source.totalTime ?? 0),
      totalPoint: Number(source.totalPoint ?? 0),
      isTryListening: Boolean(source.isTryListening),
      productId: source.productId ? String(source.productId) : undefined,
    };
  }

  private normalizeEbookInfo(value: unknown): BookEbookAccessInfo | undefined {
    if (!value || typeof value !== "object") {
      return undefined;
    }

    const source = value as Record<string, unknown>;

    return {
      enabled: Boolean(source.enabled),
      hasAccess: Boolean(source.hasAccess),
      canPurchase: Boolean(source.canPurchase),
      price: Number(source.price ?? 0),
      pageCount: Number(source.pageCount ?? 0),
      status: source.status ? String(source.status) : undefined,
    };
  }

  private sanitizeUrlValue(value: unknown): string {
    if (typeof value !== "string") {
      return "";
    }

    let output = value
      .trim()
      .replace(/\\"/g, "\"")
      .replace(/\\'/g, "'")
      .replace(/&quot;/gi, "\"")
      .replace(/&#34;/gi, "\"")
      .replace(/&apos;/gi, "'");

    if (!output) {
      return "";
    }

    const parsed = (() => {
      try {
        return JSON.parse(output);
      } catch (_error) {
        return null;
      }
    })();

    if (typeof parsed === "string") {
      output = parsed.trim();
    }

    while (
      output.length >= 2 &&
      ((output.startsWith("\"") && output.endsWith("\"")) ||
        (output.startsWith("'") && output.endsWith("'")))
    ) {
      output = output.slice(1, -1).trim();
    }

    return output;
  }

  private getMediaBaseUrl(): string {
    if (typeof window === "undefined") {
      return "";
    }

    const valueFromLocalStorage = window.localStorage.getItem(
      this.APP_BUCKET_URL_STORAGE_KEY
    );
    const legacyValueFromLocalStorage = window.localStorage.getItem(
      this.APP_BASE_URL_STORAGE_KEY
    );
    const valueFromStorageService = StorageService.get(this.BASE_URL_STORAGE_KEY);

    return this.sanitizeUrlValue(
      valueFromLocalStorage ||
        legacyValueFromLocalStorage ||
        valueFromStorageService ||
        ""
    );
  }

  private toAbsoluteMediaUrl(path: unknown): string {
    const mediaPath = this.sanitizeUrlValue(path);

    if (!mediaPath) {
      return "";
    }

    if (
      mediaPath.startsWith("http://") ||
      mediaPath.startsWith("https://") ||
      mediaPath.startsWith("data:") ||
      mediaPath.startsWith("blob:") ||
      mediaPath.startsWith("//")
    ) {
      return mediaPath;
    }

    const mediaBaseUrl = this.getMediaBaseUrl();

    if (!mediaBaseUrl) {
      return mediaPath;
    }

    try {
      const normalizedBaseUrl = mediaBaseUrl.endsWith("/")
        ? mediaBaseUrl
        : `${mediaBaseUrl}/`;

      return new URL(mediaPath, normalizedBaseUrl).toString();
    } catch (_) {
      const normalizedBaseUrl = mediaBaseUrl.endsWith("/")
        ? mediaBaseUrl.slice(0, -1)
        : mediaBaseUrl;
      const normalizedPath = mediaPath.startsWith("/")
        ? mediaPath
        : `/${mediaPath}`;

      return `${normalizedBaseUrl}${normalizedPath}`;
    }
  }

  private toArray(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);

        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    }

    return [];
  }

  private toMediaStringArray(value: unknown): string[] {
    const urlsFromArray = this.toArray(value)
      .map((item) => this.toAbsoluteMediaUrl(item))
      .filter((item) => !!item);

    if (urlsFromArray.length) {
      return urlsFromArray;
    }

    const singleUrl = this.toAbsoluteMediaUrl(value);

    return singleUrl ? [singleUrl] : [];
  }

  private toBookNotes(value: unknown): BookNoteDetail[] {
    return this.toArray(value)
      .map((item) => {
        if (!item || typeof item !== "object") {
          return null;
        }

        const source = item as Record<string, unknown>;

        return {
          id: Number(source.id ?? 0),
          textSelected: String(source.textSelected ?? ""),
          textNote: String(source.textNote ?? ""),
        };
      })
      .filter((item): item is BookNoteDetail => item !== null);
  }

  private toHighlightTexts(value: unknown): BookElementHighlightTextDetail[] {
    const outputs: BookElementHighlightTextDetail[] = [];

    this.toArray(value).forEach((item) => {
      if (!item || typeof item !== "object") {
        return;
      }

      const source = item as Record<string, unknown>;

      outputs.push({
        text: source.text ? String(source.text) : undefined,
        duration:
          typeof source.duration === "number" ? source.duration : undefined,
        from: typeof source.from === "number" ? source.from : undefined,
        to: typeof source.to === "number" ? source.to : undefined,
        mediaLink: source.mediaLink
          ? this.toAbsoluteMediaUrl(source.mediaLink)
          : undefined,
        mediaType: source.mediaType ? String(source.mediaType) : undefined,
      });
    });

    return outputs;
  }

  private toAudios(value: unknown): BookAudioDetail[] {
    const outputs: BookAudioDetail[] = [];

    this.toArray(value).forEach((item) => {
      if (!item || typeof item !== "object") {
        return;
      }

      const source = item as Record<string, unknown>;

      outputs.push({
        name: source.name ? String(source.name) : undefined,
        audio: source.audio ? this.toAbsoluteMediaUrl(source.audio) : undefined,
        endpointConvertId:
          typeof source.endpointConvertId === "number"
            ? source.endpointConvertId
            : undefined,
      });
    });

    return outputs;
  }

  private getFirstAudioLink(audios: BookAudioDetail[]): string {
    return (
      audios.find((item) => typeof item.audio === "string" && item.audio)?.audio ??
      ""
    );
  }

  private mapBookElementChild(raw: BookElementV3Raw): BookElementChildrenDetail {
    const audios = this.toAudios(raw.audios);
    const rawLinkAudio = this.toAbsoluteMediaUrl(raw.linkAudio);

    return {
      id: Number(raw.id ?? 0),
      name: String(raw.name ?? ""),
      description: raw.description ? String(raw.description) : null,
      content: String(raw.content ?? ""),
      linkAudio: this.getFirstAudioLink(audios) || rawLinkAudio,
      level: Number(raw.level ?? 0),
      order: Number(raw.order ?? 0),
      child: this.toArray(raw.child).map((item) =>
        this.mapBookElementChild(item as BookElementV3Raw)
      ),
      isLock: Boolean(raw.isLock),
      point: Number(raw.point ?? 0),
      bookNotes: this.toBookNotes(raw.bookNotes),
      audioDuration: Number(raw.audioDuration ?? 0),
      audios,
      highlightTexts: this.toHighlightTexts(raw.highlightTexts),
    };
  }

  private mapBookElement(raw: BookElementV3Raw): BookElementDetail {
    const audios = this.toAudios(raw.audios);
    const rawLinkAudio = this.toAbsoluteMediaUrl(raw.linkAudio);

    return {
      id: Number(raw.id ?? 0),
      name: String(raw.name ?? ""),
      description: raw.description ? String(raw.description) : null,
      content: String(raw.content ?? ""),
      linkAudio: this.getFirstAudioLink(audios) || rawLinkAudio,
      level: Number(raw.level ?? 0),
      order: Number(raw.order ?? 0),
      child: this.toArray(raw.child).map((item) =>
        this.mapBookElementChild(item as BookElementV3Raw)
      ),
      isLock: Boolean(raw.isLock),
      point: Number(raw.point ?? 0),
      bookNotes: this.toBookNotes(raw.bookNotes),
      audioDuration: Number(raw.audioDuration ?? 0),
      audios,
      highlightTexts: this.toHighlightTexts(raw.highlightTexts),
    };
  }

  private toAuthors(value: unknown): BookDetailAuthor[] {
    return this.toArray(value)
      .map((item) => {
        if (!item || typeof item !== "object") {
          return null;
        }

        const source = item as Record<string, unknown>;

        return {
          id: Number(source.id ?? 0),
          fullName: String(source.fullName ?? ""),
          thumbnail: this.toAbsoluteMediaUrl(source.thumbnail),
        };
      })
      .filter((item): item is BookDetailAuthor => item !== null);
  }

  private toTop30Books(value: unknown): Book[] {
    return this.toArray(value)
      .map((item) => {
        if (!item || typeof item !== "object") {
          return null;
        }

        const source = item as Book;

        return {
          ...source,
          thumbnails: this.toMediaStringArray(
            (item as Record<string, unknown>).thumbnails
          ),
        };
      })
      .filter((item): item is Book => item !== null);
  }

  private normalizeBookDetailV3(data: BookDetailV3Raw): BookDetail {
    const authors = this.toAuthors(data.authors);
    const author =
      authors.length > 0
        ? authors
            .map((item) => item.fullName)
            .filter((item) => !!item)
            .join(", ")
        : String(data.author ?? "");

    const categories = this.toArray(data.categories)
      .map((item) => {
        if (!item || typeof item !== "object") {
          return null;
        }

        const source = item as Record<string, unknown>;

        return {
          categoryId: Number(source.categoryId ?? 0),
          categoryName: String(source.categoryName ?? ""),
          categoryTitle:
            source.categoryTitle !== null && source.categoryTitle !== undefined
              ? String(source.categoryTitle)
              : null,
        };
      })
      .filter((item): item is BookCategory => item !== null);

    const insight: BookInsight = {
      likeCount: Number(data.insight?.likeCount ?? 0),
      commentCount: Number(data.insight?.commentCount ?? 0),
      listenCount: Number(data.insight?.listenCount ?? 0),
      shareCount: Number(data.insight?.shareCount ?? 0),
      viewCount: Number(data.insight?.viewCount ?? 0),
    };

    return {
      id: Number(data.id ?? 0),
      available: Boolean(data.available ?? data.isAvailable),
      isAvailable: Boolean(data.isAvailable ?? data.available),
      linkStore: String(data.linkStore ?? ""),
      categories,
      backgroundMusic: {
        id: Number(data.backgroundMusic?.id ?? 0),
        name: String(data.backgroundMusic?.name ?? ""),
        link: this.toAbsoluteMediaUrl(data.backgroundMusic?.link),
      },
      name: String(data.name ?? ""),
      description: String(data.description ?? ""),
      price: Number(data.price ?? 0),
      oldPrice: Number(data.oldPrice ?? 0),
      bookOption: Number(data.bookOption ?? 1),
      thumbnails: this.toMediaStringArray(data.thumbnails),
      author,
      authors,
      isPopular: Boolean(data.isPopular),
      isFavorite: Boolean(data.isFavorite),
      isTryListening: Boolean(data.isTryListening),
      releaseType: Number(data.releaseType ?? 0),
      totalPoint: Number(data.totalPoint ?? 0),
      totalTime: Number(data.totalTime ?? 0),
      elements: this.toArray(data.elements).map((item) =>
        this.mapBookElement(item as BookElementV3Raw)
      ),
      top30Book: this.toTop30Books(data.top30Book),
      insight,
      mediaType: Number(data.mediaType ?? BookMediaType.AUDIO),
      audio: this.normalizeAudioInfo(data.audio),
      ebook: this.normalizeEbookInfo(data.ebook),
    };
  }

  public getList(requestParams?: RequestParams, signal?: Signal) {
    return HttpService.get<GetListResponse<Book>>("/user/api/v1/books", {
      queryParams: { ...requestParams },
      signal,
    });
  }

  public searchBooks(requestParams?: RequestParams, signal?: Signal) {
    return HttpService.get<GetListResponse<Book>>("/user/api/v2/books", {
      queryParams: { ...requestParams },
      signal,
    });
  }

  public getBooksByTags(signal?: Signal) {
    return HttpService.get<GetDetailResponse<BooksByTags>>(
      "/user/api/v1/book-apps",
      {
        signal,
      }
    );
  }

  public getBooksByType(
    typeOfBook: TypeOfBook,
    requestParams?: RequestParams,
    signal?: Signal
  ) {
    return HttpService.get<GetListResponse<Book>>(
      "/user/api/v1/book-apps/type",
      {
        queryParams: { ...requestParams, type: typeOfBook },
        signal,
      }
    );
  }

  public getBooksByCategory(
    categoryId: number | string,
    requestParams?: RequestParams,
    signal?: Signal
  ) {
    return HttpService.get<GetListResponse<Book>>(
      "/user/api/v1/book-apps/category",
      {
        queryParams: { ...requestParams, categoryId },
        signal,
      }
    );
  }

  public getBookHistory(signal?: Signal) {
    return HttpService.get<GetListResponse<Book>>(
      "/user/api/v2/libraries/history",
      {
        signal,
      }
    );
  }

  public getBookFavorite(signal?: Signal) {
    return HttpService.get<GetListResponse<Book>>(
      "/user/api/v2/libraries/favorite",
      {
        signal,
      }
    );
  }

  public createFavorite(bookId: number) {
    return HttpService.post<GetAllResponse<Book>>(
      "/user/api/v1/libraries/favorite",
      {
        body: {
          bookId,
        },
      }
    );
  }

  public deleteFavorite(bookId: number) {
    return HttpService.delete<GetAllResponse<Book>>(
      "/user/api/v1/libraries/favorite",
      {
        body: {
          bookId,
        },
      }
    );
  }

  public getBookPurchased(signal?: Signal) {
    return HttpService.get<GetListResponse<Book>>(
      "/user/api/v2/books/purchased/list",
      {
        signal,
      }
    );
  }

  public async getBookById(
    bookId: number,
    signal?: Signal,
    options?: BookDetailRequestOptions
  ) {
    const shouldUseCache = !!options?.useCache && !options?.forceRefresh;
    const cachedBookDetail = this.bookDetailCache.get(bookId);

    if (shouldUseCache && cachedBookDetail) {
      return cachedBookDetail;
    }

    const response = await HttpService.get<GetDetailResponse<BookDetailV3Raw>>(
      `/user/api/v4/books/${bookId}`,
      { signal }
    );
    const normalizedResponse = {
      ...response,
      data: this.normalizeBookDetailV3(response.data),
    };

    this.bookDetailCache.set(bookId, normalizedResponse);

    return normalizedResponse;
  }

  public async get(
    bookId: number,
    signal?: Signal,
    options?: BookDetailRequestOptions
  ) {
    const shouldUseCache = !!options?.useCache && !options?.forceRefresh;
    const cachedBookDetail = this.bookDetailCache.get(bookId);

    if (shouldUseCache && cachedBookDetail) {
      return cachedBookDetail;
    }

    const response = await HttpService.get<GetDetailResponse<BookDetailV3Raw>>(
      `/user/api/v4/books/${bookId}`,
      {
        signal,
      }
    );
    const normalizedResponse = {
      ...response,
      data: this.normalizeBookDetailV3(response.data),
    };

    this.bookDetailCache.set(bookId, normalizedResponse);

    return normalizedResponse;
  }

  public create(createBook: CreateBook) {
    return HttpService.post("/user/api/v1/books", {
      body: { ...createBook },
      requestContentType: RequestContentType.MULTIPART,
    });
  }

  public update(bookId: number, updateBook: CreateBook) {
    return HttpService.patch(`/user/api/v1/books/${bookId}`, {
      body: { ...updateBook },
      requestContentType: RequestContentType.MULTIPART,
    });
  }

  public delete(bookId: number) {
    return HttpService.delete(`/user/api/v1/books/${bookId}`);
  }

  public getBookCategoryFavorite(
    requestParams?: RequestParams,
    signal?: Signal
  ) {
    return HttpService.get<GetListResponse<Book>>(
      "/user/api/v2/books/category-favorite",
      {
        queryParams: { ...requestParams },
        signal,
      }
    );
  }

  public getBookOption(
    requestParams?: GetBookOptionRequestParams,
    signal?: Signal
  ) {
    return HttpService.get<GetListResponse<Book>>(
      "/user/api/v2/books/books/book-option",
      {
        queryParams: { ...requestParams },
        signal,
      }
    );
  }

  public unlockElement(elementId: number) {
    return HttpService.post(
      `/user/api/v2/books/unlock-book-element/${elementId}`
    );
  }

  public createEbookSession(bookId: number) {
    return HttpService.post<GetDetailResponse<EbookSession>>(
      `/user/api/v4/books/${bookId}/ebook/session`
    );
  }

  public getEbookManifest(bookId: number, token: string) {
    return HttpService.get<GetDetailResponse<EbookManifest>>(
      `/user/api/v4/books/${bookId}/ebook/manifest`,
      {
        queryParams: { token },
      }
    );
  }

  public getEbookContent(bookId: number, resourceId: number, token: string) {
    return HttpService.get<GetDetailResponse<EbookContent>>(
      `/user/api/v4/books/${bookId}/ebook/content/${resourceId}`,
      {
        queryParams: { token },
      }
    );
  }

  public async getEbookPdf(bookId: number, token: string) {
    const accessToken = StorageService.get(ACCESS_TOKEN_KEY);
    const response = await axios.get<Blob>(
      `${Environment.BASE_API}/user/api/v4/books/${bookId}/ebook/pdf`,
      {
        responseType: "blob",
        params: { token },
        headers: accessToken
          ? {
              Authorization: `Bearer ${accessToken}`,
            }
          : undefined,
      }
    );

    return response.data;
  }

  public saveEbookProgress(payload: {
    bookId: number;
    currentPage: number;
    progressPercent: number;
    lastPositionJson?: Record<string, unknown>;
  }) {
    return HttpService.post<GetDetailResponse<EbookProgress>>(
      "/user/api/v4/ebook-progress",
      {
        body: payload,
      }
    );
  }

  public getEbookProgress(bookId: number) {
    return HttpService.get<GetDetailResponse<EbookProgress>>(
      `/user/api/v4/ebook-progress/${bookId}`
    );
  }

  public getEbookNotes(bookId: number, pageNum = 1, pageSize = 100) {
    return HttpService.get<GetListResponse<EbookNote>>("/user/api/v4/ebook-notes", {
      queryParams: { bookId, pageNum, pageSize },
    });
  }

  public createEbookNote(payload: {
    bookId: number;
    pageNo: number;
    textSelected: string;
    textNote: string;
    anchorJson?: Record<string, unknown>;
  }) {
    return HttpService.post<GetDetailResponse<{ id: number }>>(
      "/user/api/v4/ebook-notes",
      {
        body: payload,
      }
    );
  }

  public updateEbookNote(id: number, payload: { textNote: string }) {
    return HttpService.patch<GetDetailResponse<{ message?: string }>>(
      `/user/api/v4/ebook-notes/${id}`,
      {
        body: payload,
      }
    );
  }

  public deleteEbookNote(id: number) {
    return HttpService.delete<GetDetailResponse<{ message?: string }>>(
      `/user/api/v4/ebook-notes/${id}`
    );
  }
}

const BookService = new _BookService();

export default BookService;
